import { ParkingHistory, Reservation, VehicleInfoOversizeTypeEnum } from '@parkingbase/ecommerce-api';
import { resolveRecordedTicketExit } from '@/lib/ticket-exit';


/**
 * Builds the touchless-ticket QR payload for an open ticket's View Pass.
 *
 * ## Invariants:
 * - Encodes the customer-facing touchless page on THIS reservation site
 *   (`{baseUrl}/touchless/{id}`), never a hardcoded `prkng.co` claim tag.
 *   Why: this QR is scanned both by the POS scanner (which resolves the ticket
 *   id from any URL) AND by the customer's own phone camera (which opens it as
 *   a plain web URL). A hardcoded `https://prkng.co/{id}` only resolves for a
 *   company whose reservationUrl IS `prkng.co`; for any other company a customer
 *   self-scan (the Zone / self-service case) opens prkng.co's expired-magic-link
 *   404 instead of the ticket page (PB-3402). Mirrors the company-aware QR the
 *   POS prints in `ZebraService` (`{reservationUrl}/touchless/{id}`).
 * - `baseUrl` is the absolute base URL of this reservation site, including any
 *   `/c/{companyId}` multi-tenant prefix (from `getBaseUrl(origin, pathname)`);
 *   returns `undefined` when it is missing so the modal renders no broken QR.
 * - The id is `transactionId`, not `documentId`. For an open ticket the
 *   parking-history response carries the ticket's own doc id in `transactionId`
 *   (== `ticket.id`); the top-level `documentId` is the parking-history wrapper
 *   doc, which the touchless page / POS cannot resolve.
 */
export function buildTicketClaimQrValue(item: ParkingHistory, baseUrl: string): string | undefined {
  const ticketDocumentId = item.transactionId;
  if (!ticketDocumentId || !baseUrl) return undefined;
  const base = baseUrl.replace(/\/+$/, '');
  return `${base}/touchless/${ticketDocumentId}`;
}

export function mapParkingHistoryToReservation(item: ParkingHistory): Partial<Reservation> {
  const isOpen = !!item.isOpen;
  const ticket = item.ticket;
  const transaction = item.transaction;
  const source = isOpen ? ticket : transaction;

  const enterRaw = source?.enter;
  const exitRaw = source?.exit;
  const enter = enterRaw != null ? new Date(enterRaw).toISOString() : undefined;
  // Exit is judged by its VALUE, never by `isOpen`. Why: paying a zone / touchless ticket writes
  // the paid-until into the ticket's own exit time, so a prepaid ticket has a real exit while it is
  // still open. Suppressing exit for every open ticket therefore hid the one thing the parker had
  // paid for, and showed "Active / No exit specified" on a session with a known end.
  //
  // An unpaid POS ticket reports exit as epoch 0 — the open sentinel, not a 1969 timestamp — so it
  // still resolves to no exit, which is correct: you pay those on the way out. The epoch-0 rule is
  // shared with the touchless screen so the two cannot disagree about the same ticket.
  const exit = resolveRecordedTicketExit(exitRaw);

  return {
    reservationId: item.transactionId || item.documentId,
    company: item.companyId || item.company,
    vendorReservationId: ticket?.ticket || transaction?.ticketNumber?.toString(),
    garageName: item.garageName,
    garageId: item.garageId,
    checkinAfterTime: enter,
    checkoutBeforeTime: exit,
    customerName: source?.name || '',
    status: isOpen ? 'CHECKIN' : 'CHECKOUT',
    reservationType: item.monthly ? 'MONTHLY' : 'TRANSIENT',
    vehicleInfo: {
      vehicleLicensePlate: source?.plate || '',
      vehicleColor: source?.color || '',
      vehicleMake: source?.make || '',
      vehicleModel: source?.model || '',
      vehicleState: source?.state || '',
      vehicleName: '',
      oversizeType: VehicleInfoOversizeTypeEnum.STANDARD,
    },
  };
}
